import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SessionLoadingScreen from 'src/shared/components/SessionLoadingScreen';
import { sessionManager } from 'src/utils/sessionManager';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const AUTH_CHECK_INTERVAL = 5 * 60 * 1000;

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const lastCheckRef = useRef<number>(0);
  const hasInitializedRef = useRef(false);

  const checkAuth = useCallback(
    async (force = false) => {
      const now = Date.now();
      if (!force && hasInitializedRef.current && now - lastCheckRef.current < AUTH_CHECK_INTERVAL) {
        return;
      }

      try {
        const isValid = await sessionManager.isSessionValid();
        lastCheckRef.current = Date.now();
        setIsAuthenticated(isValid);

        if (isValid) {
          sessionManager.refreshSession();
        } else {
          navigate('/login', {
            replace: true,
            state: { from: location.pathname },
          });
        }
      } catch {
        navigate('/login', { replace: true });
      } finally {
        if (!hasInitializedRef.current) {
          hasInitializedRef.current = true;
          setIsLoading(false);
        }
      }
    },
    [navigate, location.pathname]
  );

  useEffect(() => {
    checkAuth(!hasInitializedRef.current);
  }, [checkAuth]);

  useEffect(() => {
    if (!hasInitializedRef.current) return;

    const interval = setInterval(() => {
      checkAuth(true);
    }, AUTH_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [checkAuth]);

  if (isLoading) {
    return <SessionLoadingScreen stage="auth" />;
  }

  return isAuthenticated ? <>{children}</> : null;
};

export default ProtectedRoute;
